import React, { FC } from "react";
import { Form, Formik } from "formik";
import { ButtonPrimary, ButtonSecondary, Input, Textarea, ErrorMessage } from "@shared/components";
import { Modal } from "@shared/components/Modal";
import * as Yup from "yup";
import { currencyFormatUsd } from "@shared/utils/currency";

import SelectInput from "../Field/Select/Select";

import "./index.scss";

type FormValues = { amount?: string; type: string; reason: string };

export type ModalAdjustAccountBalanceProps = {
  data?: { credits: number };
  onClose: () => void;
  onSuccess: (values: FormValues) => void;
};

const formValidationSchema = Yup.object().shape({
  amount: Yup.number()
    .transform((value) => (isNaN(value) ? "" : value))
    .positive("Please enter a positive number")
    .max(100_000, "Please enter a value less than 100,000")
    .required("Please enter the value to adjust the balance"),
  type: Yup.string().oneOf(["increase", "decrease"]).required("Please select the adjustment type"),
  reason: Yup.string()
    .trim()
    .max(250, "Please keep adjustment reason under 250 characters")
    .required("Please provide an adjustment reason"),
});

export const ModalAdjustAccountBalance: FC<ModalAdjustAccountBalanceProps> = (props) => {
  const { data, onClose, onSuccess } = props;

  const operations = [
    { label: "Add funds", value: "increase" },
    { label: "Remove funds", value: "decrease" },
  ];

  const initialValues: FormValues = {
    amount: "",
    type: operations[0].value,
    reason: "",
  };

  const handleSubmit = async (values: FormValues) => {
    onSuccess(values);
  };

  return (
    <Modal>
      <Formik
        validationSchema={formValidationSchema}
        initialValues={initialValues}
        enableReinitialize={true}
        validateOnBlur={false}
        onSubmit={handleSubmit}
      >
        {({ values, errors, touched, isSubmitting, isValid, setFieldValue, handleChange, handleSubmit }) => {
          const calculateFinalBalance = () => {
            try {
              const currentBalanceInCents = data?.credits || 0;
              const currentBalanceInUSD = currentBalanceInCents / 100;
              const amountToAdjustInCents = Math.floor((Number(values.amount) || 0) * 100);

              const newBalanceInCents =
                values.type === "increase"
                  ? currentBalanceInCents + amountToAdjustInCents
                  : currentBalanceInCents - amountToAdjustInCents;

              const newBalanceInUsd = newBalanceInCents / 100;

              return Number.isFinite(newBalanceInUsd) ? newBalanceInUsd : currentBalanceInUSD;
            } catch (error) {
              return 0;
            }
          };

          const handleChangeAmount = (evt: React.ChangeEvent<HTMLInputElement>) => {
            let inputValue = (evt.target as HTMLInputElement).value;

            if (inputValue === "") {
              return setFieldValue("amount", "", true);
            }

            if (inputValue === ".") {
              return setFieldValue("amount", "0.", true);
            }

            inputValue = inputValue.replace(/[^\d.]/g, "");

            const isValidFormat = /^(\d+(\.\d{0,2})?|\.\d{1,2})$/.test(inputValue);

            const numericValue = parseFloat(inputValue);

            const isInRage = numericValue >= 0 && numericValue <= 999_999_999;
            if (isValidFormat && isInRage && inputValue.includes(".")) {
              const [numeric, decimal] = inputValue.split(".");
              return setFieldValue("amount", `${numeric}.${decimal.slice(0, 2)}`, true);
            } else if (isValidFormat && isInRage) {
              return setFieldValue("amount", numericValue, true);
            }

            setFieldValue("amount", values.amount, true);
          };

          return (
            <Form onSubmit={handleSubmit}>
              <Modal.Section>
                <Modal.Header>
                  <Modal.Title>Adjust Balance</Modal.Title>
                </Modal.Header>

                <Modal.Content>
                  <div className="modal-content-inner">
                    <div className="modal-content-inner-item">
                      <SelectInput
                        name="type"
                        value={operations.find((o) => o.value === values.type)}
                        options={operations}
                        onChange={(value) => {
                          setFieldValue("type", value, true);
                        }}
                      />
                    </div>

                    <div className="modal-content-inner-item">
                      <Input
                        name="amount"
                        type="text"
                        placeholder="$ Enter Amount"
                        max={100_000}
                        value={values.amount ?? ""}
                        onChange={handleChangeAmount}
                      />
                      <ErrorMessage isTouched={touched.amount} error={errors.amount} />
                    </div>
                  </div>

                  <div>
                    <Textarea
                      name="reason"
                      label="Reason"
                      value={values.reason}
                      maxLength={250}
                      rows={3}
                      onChange={handleChange}
                    />
                    <ErrorMessage isTouched={touched.reason} error={errors.reason} />
                  </div>
                  <div className="balance-changes-hint">
                    {values.amount ? (
                      <Modal.Text>
                        The total balance of the FlickWallet after this change will be{" "}
                        <b>{currencyFormatUsd(calculateFinalBalance(), { maximumFractionDigits: 2 })}</b>
                      </Modal.Text>
                    ) : (
                      ""
                    )}
                  </div>
                </Modal.Content>

                <Modal.Actions>
                  <ButtonSecondary onClick={onClose}>Cancel</ButtonSecondary>
                  <ButtonPrimary type="submit" disabled={isSubmitting || !isValid}>
                    Confirm
                  </ButtonPrimary>
                </Modal.Actions>
              </Modal.Section>
            </Form>
          );
        }}
      </Formik>
    </Modal>
  );
};
